import {Exclude, instanceToPlain} from 'class-transformer';
import {IsEnum, IsString, Validate, ValidationArguments, ValidatorConstraint, ValidatorConstraintInterface} from 'class-validator';

import {AppPlaceholders, isAppPlaceholder, IsJsonString, uuidv4} from '@shared-types';

import {TriggerActionEntityClassMetadata, TriggerActionType} from './trigger.action.types';

function isUUID(value: string): boolean {
	return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

@ValidatorConstraint({name: 'IsPlaceholderOrEmailOrUserIdOrUserId', async: false})
export class IsPlaceholderOrEmailOrUserId implements ValidatorConstraintInterface {
	private emailRegex = new RegExp('[^@\\s]+@[^@\\s]+\\.[^@\\s]+');

	validate(value: any, validationArguments?: ValidationArguments): boolean {
		return isAppPlaceholder(value) || this.emailRegex.test(value) || isUUID(value);
	}
	defaultMessage?(validationArguments?: ValidationArguments): string {
		return 'trigger.errors.invalidEmailOrPlaceholder';
	}
}

declare type ConstructorOf<T> = new (...args: any[]) => T;

/* -------------------------------------------------------------------------------------------------------------- */

export abstract class TriggerActionEntity {
	static _uniqueId = '';

	@IsEnum(TriggerActionType.Enum, {message: 'trigger.errors.invalidActionType'}) public type: TriggerActionType;
	public static readonly PLACEHOLDERS: string[] = [];

	protected constructor(type: TriggerActionType) {
		this.type = type;
	}

	static create<T extends TriggerActionEntity>(type: TriggerActionType, init?: Partial<T>): T {
		const action = TriggerActionEntity.getActionClass(type);
		return new action(init) as T;
	}

	@Exclude() public readonly validity: ClassValidatorSignals = classValidatorSignals.bind(this)();

	private static getActionClass(type: TriggerActionType): ConstructorOf<TriggerActionEntity> {
		switch (type) {
			case TriggerActionType.Notify:
				return NotifyAction;
			case TriggerActionType.Agent:
				return AgentAction;
			case TriggerActionType.Email:
				return EmailAction;
			case TriggerActionType.Cancel:
				return CancelAction;
			case TriggerActionType.Alert:
				return AlertAction;
			case TriggerActionType.Gtm:
				return GtmAction;
			default:
				throw new Error(`Unsupported action type: ${type}`);
		}
	}

	abstract toJSON(): TriggerActionEntity;
}

/**
 * Notify action
 * @extends TriggerActionEntity
 * @public
 * @class NotifyAction
 */
@GroupDecorator('trigger-action-entity', true)
@ClassMetaData<TriggerActionEntityClassMetadata>({
	name: 'notify-action',
	label: 'pages.cw.eventactions.triggertypes.notify',
	summary: `components.trigger-editor.action.notify-action.summary`,
	valueClass: {} as any,
})
export class NotifyAction extends TriggerActionEntity {
	static override _uniqueId: string = uuidv4();

	constructor(init?: Partial<TriggerActionEntity>) {
		super(TriggerActionType.Notify);
		Object.assign(this, init);
	}
	toJSON(): NotifyAction {
		return instanceToPlain(this, {}) as NotifyAction;
	}
}

/**
 * Agent action
 * @extends TriggerActionEntity
 * @public
 * @class AgentAction
 * @property {string} code - Agent code
 */
@GroupDecorator('trigger-action-entity', true)
@ClassMetaData<TriggerActionEntityClassMetadata>({
	name: 'agent-action',
	label: 'pages.cw.eventactions.triggertypes.agent',
	summary: `components.trigger-editor.action.agent-action.summary.[action.code].<*,:>`,
	valueClass: {
		selector: 'dc-trigger-action-input-value',
		params: {
			label: 'pages.cw.triggerwizard.agentph',
			actionProp: 'code',
			type: 'text',
			required: false,
		},
	},
})
export class AgentAction extends TriggerActionEntity {
	static override _uniqueId: string = uuidv4();

	@IsString({message: 'trigger.errors.invalidAgentCode'})
	public code = '';

	@Exclude() public override readonly validity: ClassValidatorSignals = classValidatorSignals.bind(this)();

	constructor(init?: Partial<TriggerActionEntity>) {
		super(TriggerActionType.Agent);
		Object.assign(this, init);
	}

	toJSON(): AgentAction {
		return instanceToPlain(this, {}) as AgentAction;
	}

	public static isAgentAction(action: TriggerActionEntity, validateCode = false): action is AgentAction {
		return action.type === TriggerActionType.Agent && (!validateCode || (action as AgentAction)?.code !== '');
	}
}

/**
 * Email action
 * @extends TriggerActionEntity
 * @public
 * @class EmailAction
 * @property {string} recipient - Email recipient
 */
@GroupDecorator('trigger-action-entity', true)
@ClassMetaData<TriggerActionEntityClassMetadata>({
	name: 'email-action',
	label: 'components.trigger-editor.action.email-action.label',
	summary: `components.trigger-editor.action.email-action.summary.[action.recipient]`, // creator placeholder or 'users'
	valueClass: {
		selector: 'dc-user-search',
		params: {
			label: 'pages.cw.triggerwizard.emailph',
			actionProp: 'recipient',
			required: true,
		},
	},
})
export class EmailAction extends TriggerActionEntity {
	static override _uniqueId: string = uuidv4();

	@Exclude() public override readonly validity: ClassValidatorSignals = classValidatorSignals.bind(this)();

	public static override readonly PLACEHOLDERS: string[] = [AppPlaceholders.creator_email] as const;

	@Validate(IsPlaceholderOrEmailOrUserId)
	public recipient: enumType<typeof EmailAction.PLACEHOLDERS> | Email;

	constructor(init?: Partial<TriggerActionEntity>) {
		super(TriggerActionType.Email);
		Object.assign(this, init);
	}

	public toJSON(): EmailAction {
		return instanceToPlain(this, {}) as EmailAction;
	}
}

/**
 * Cancel action
 * @extends TriggerActionEntity
 * @public
 * @class CancelAction
 */
@GroupDecorator('trigger-action-entity', true)
@ClassMetaData<TriggerActionEntityClassMetadata>({
	name: 'cancel-action',
	label: 'pages.cw.eventactions.triggertypes.cancel',
	summary: `components.trigger-editor.action.cancel-action.summary`,
	valueClass: {} as any,
})
export class CancelAction extends TriggerActionEntity {
	static override _uniqueId: string = uuidv4();

	@Exclude() public override readonly validity: ClassValidatorSignals = classValidatorSignals.bind(this)();

	constructor(init?: Partial<TriggerActionEntity>) {
		super(TriggerActionType.Cancel);
		Object.assign(this, init);
	}

	public toJSON(): CancelAction {
		return instanceToPlain(this, {}) as CancelAction;
	}
}

/**
 * Alert action
 * @extends TriggerActionEntity
 * @public
 * @class AlertAction
 * @property {enumType<typeof this.PLACEHOLDERS> | Email} recipient - Alert recipient email or allowed placeholders
 */
@GroupDecorator('trigger-action-entity', true)
@ClassMetaData<TriggerActionEntityClassMetadata>({
	name: 'alert-action',
	label: 'components.trigger-editor.action.alert-action.label',
	summary: `components.trigger-editor.action.alert-action.summary.[action.recipient]`,
	valueClass: {
		selector: 'dc-user-search',
		params: {
			label: 'pages.cw.triggerwizard.alertph',
			actionProp: 'recipient',
			required: true,
		},
	},
})
export class AlertAction extends TriggerActionEntity {
	static override _uniqueId: string = uuidv4();

	@Exclude() public override readonly validity: ClassValidatorSignals = classValidatorSignals.bind(this)();

	public static override readonly PLACEHOLDERS: string[] = [AppPlaceholders.creator_email] as const;

	@Validate(IsPlaceholderOrEmailOrUserId)
	public recipient: enumType<typeof AlertAction.PLACEHOLDERS> | Email;

	constructor(init?: Partial<TriggerActionEntity>) {
		super(TriggerActionType.Alert);
		Object.assign(this, init);
	}

	toJSON(): AlertAction {
		return instanceToPlain(this, {}) as AlertAction;
	}
}

/**
 * Gtm action - Google Tag Manager action
 * @extends TriggerActionEntity
 * @public
 * @class GtmAction
 * @property {`JsonString`} data - Gtm data
 */
@GroupDecorator('trigger-action-entity', true)
@ClassMetaData<TriggerActionEntityClassMetadata>({
	name: 'gtm-action',
	label: 'components.trigger-editor.action.gtm-action.summary',
	summary: `components.trigger-editor.action.gtm-action.summary`,
	valueClass: {
		selector: 'dc-trigger-action-gtm-data',
		params: {},
	},
})
export class GtmAction extends TriggerActionEntity {
	static override _uniqueId: string = uuidv4();
	@Exclude() public override readonly validity: ClassValidatorSignals = classValidatorSignals.bind(this)();

	@Validate(IsJsonString, {message: 'trigger.errors.invalidJsonData'})
	data: JsonString;

	constructor(init?: Partial<GtmAction>) {
		super(TriggerActionType.Gtm);
		Object.assign(this, init);
	}

	toJSON(): GtmAction {
		return instanceToPlain(this, {}) as GtmAction;
	}
}
